import { Controller } from "@hotwired/stimulus"
import { isFocusable } from "../../utils/field-visibility.util"

// Every shape a server-rendered field error takes in this app, in one place.
// `.form-hint--error` is what `shared/_error_form_field` renders (document
// fields, modals); `.invalid-feedback` is the Bootstrap shape used by the
// document item rows; `.field_with_errors` is Rails' own wrapper and
// `.customer-field-wrapper--error` the contact picker's bespoke marker.
// `.form-hint.text-danger` is kept for the few legacy call sites still on it.
const ERROR_MARKERS = [
  ".customer-field-wrapper--error",
  ".field_with_errors",
  ".form-control.is-invalid",
  ".form-hint--error",
  ".form-hint.text-danger",
  ".invalid-feedback"
].join(", ")

const ERROR_MESSAGES = ".form-hint--error, .form-hint.text-danger, .invalid-feedback"

// Connects to data-controller="form--validation"
export default class extends Controller {
  static targets = ["field", "error"]

  connect() {
    this.#setupFieldListeners()
    // Defer to the next frame so the DOM has stabilized (Turbo stream updates,
    // Bootstrap modal transitions, async-loaded content). The failed submit
    // replaces the whole form subtree, so this controller instance is the one
    // attached to the *new* markup — the errors it finds here are the fresh
    // ones.
    requestAnimationFrame(() => this.#focusFirstError())
  }

  // Land the user on the FIRST problem, not the last one: with a long document
  // form the last error is usually far below the first field they need to fix.
  #focusFirstError() {
    const marker = this.element.querySelector(ERROR_MARKERS)
    if (!marker) return

    const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"
    marker.scrollIntoView({ behavior, block: "center" })

    const field = this.#fieldFor(marker)
    if (field) field.focus({ preventScroll: true })
  }

  // The marker is sometimes the field itself, sometimes a wrapper around it,
  // and sometimes a message rendered as a sibling column of the field.
  #fieldFor(marker) {
    if (marker.matches("input, textarea, select")) return marker

    const inside = marker.querySelector("input:not([type=hidden]), textarea, select")
    if (inside) return this.#focusable(inside)

    const row = marker.closest(".invoice__line, .lc-input, td, .row")
    return row ? this.#focusable(row.querySelector("input:not([type=hidden]), textarea, select")) : null
  }

  // A `d-none` picker (the contact select) or a readonly cell cannot take
  // focus — scrolling to the marker is all we can offer there.
  #focusable(field) {
    return isFocusable(field) ? field : null
  }

  #setupFieldListeners() {
    // Get all form controls within this element
    const formControls = this.element.querySelectorAll("input, textarea, select")

    formControls.forEach((field) => {
      this.#addFieldListener(field)
    })
  }

  #addFieldListener(field) {
    const eventType = field.tagName === "SELECT" ? "change" : "input"
    field.addEventListener(eventType, (event) => {
      this.#removeValidationError(event.target)
    })
  }

  #removeValidationError(field) {
    const container = this.#getFieldContainer(field)
    if (!container) return

    this.#removeFieldErrors(container, field)
    this.#removeErrorMessages(container)
    this.#announceCorrection(field, container)
  }

  // The sidebar's "Povinné položky" panel lives outside this form, so it
  // cannot be reached by traversal. It listens for this event and drops the
  // matching entry, which keeps the panel from contradicting the inline hint
  // we just cleared.
  #announceCorrection(field, container) {
    const fieldIds = [field.id, container.id].filter(Boolean)
    if (fieldIds.length === 0) return

    this.dispatch("fieldCorrected", { detail: { fieldIds }, target: document, prefix: "form--validation" })
  }

  #getFieldContainer(field) {
    if (field.classList.contains("form-control--invoice-item")) {
      // The cell, not the `.field_with_errors` wrapper Rails puts around the
      // input: the `.invalid-feedback` message is that wrapper's SIBLING, so
      // scoping to the wrapper left the message on screen after the fix.
      return field.closest("td") || field.closest(".field_with_errors")
    } else if (field.classList.contains("form-control--invoice-item-detail")) {
      return field.closest(".invoice__line")
    } else if (field.closest(".customer-field-wrapper")) {
      return field.closest(".customer-field-wrapper")
    } else {
      return field.closest(".lc-input")
    }
  }

  #removeFieldErrors(container, field) {
    if (container.classList.contains("field_with_errors")) {
      container.classList.remove("field_with_errors")
    } else {
      const fieldWithErrors = container.querySelectorAll(".field_with_errors")
      fieldWithErrors.forEach((wrapper) => {
        wrapper.classList.remove("field_with_errors")
      })
    }

    if (container.classList.contains("customer-field-wrapper--error")) {
      container.classList.remove("customer-field-wrapper--error")
    }

    if (field) field.classList.remove("is-invalid")
  }

  #removeErrorMessages(container) {
    const errorMessages = container.querySelectorAll(ERROR_MESSAGES)
    errorMessages.forEach((error) => {
      error.remove()
    })
  }

  // Method to manually trigger validation removal for a specific field
  clearFieldError(event) {
    this.#removeValidationError(event.target)
  }

  // Method to clear all validation errors in the form
  clearAllErrors() {
    const formControls = this.element.querySelectorAll("input, textarea, select")
    formControls.forEach((field) => {
      this.#removeValidationError(field)
    })
  }
}
